import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatAgeReductionSchedule,
  formatRateGuarantee,
  enumLabel,
  INCLUSION_LABELS,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
} from "./shared";

const ACCIDENT_COVERAGE_TYPE_LABELS: Record<string, string> = {
  onAndOffJob: "On & Off Job",
  offJob: "Off Job Only",
};

export type AccidentFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Typed-amount fields where `dollarAmount` → `$X` and non-dollar types
 * render as a label. Used for hospitalAdmission, icuAdmission, etc.
 */
const specificBenefit =
  (typeKey: string, amountKey: string): AccidentFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    if (type === "includedInLumpSumBenefitPayout")
      return "Included in lump sum benefit payout";
    if (type === "includedWithHospitalAdmission")
      return "Included w/ Hospital Admission";
    return "";
  };

/**
 * Max-benefit fields where `upToDollarAmount` → `Up to $X`.
 * Used for dislocationsMaximum, fracturesMaximum, lacerationsMaximum, coma,
 * majorDiagnosticExamXray, majorDiagnosticExamMri.
 */
const maxBenefit =
  (typeKey: string, amountKey: string): AccidentFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "upToDollarAmount") {
      const amount = formatCurrency(row[amountKey]);
      return amount ? `Up to ${amount}` : "";
    }
    return "";
  };

/** Extended (non-v5) fields — free text, passed through as-is. */
const freeText =
  (fieldKey: string): AccidentFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    return typeof value === "string" ? value : "";
  };

export const accidentFormatters: Record<string, AccidentFieldFormatter> = {
  coverageType: enumLabel("coverageType", ACCIDENT_COVERAGE_TYPE_LABELS),
  hospitalAdmission: specificBenefit(
    "hospitalAdmissionType",
    "hospitalAdmissionAmount",
  ),
  icuAdmission: specificBenefit("icuAdmissionType", "icuAdmissionAmount"),
  dailyHospitalConfinement: specificBenefit(
    "dailyHospitalConfinementType",
    "dailyHospitalConfinementAmount",
  ),
  dailyIcuConfinement: specificBenefit(
    "dailyIcuConfinementType",
    "dailyIcuConfinementAmount",
  ),
  ambulanceAir: specificBenefit("ambulanceAirType", "ambulanceAirAmount"),
  ambulanceGround: specificBenefit(
    "ambulanceGroundType",
    "ambulanceGroundAmount",
  ),
  emergencyTreatment: specificBenefit(
    "emergencyTreatmentType",
    "emergencyTreatmentAmount",
  ),
  urgentCare: specificBenefit("urgentCareType", "urgentCareAmount"),
  initialPhysicianOfficeVisit: specificBenefit(
    "initialPhysicianOfficeVisitType",
    "initialPhysicianOfficeVisitAmount",
  ),
  dislocationsMaximum: maxBenefit(
    "dislocationsMaximumType",
    "dislocationsMaximumAmount",
  ),
  fracturesMaximum: maxBenefit(
    "fracturesMaximumType",
    "fracturesMaximumAmount",
  ),
  accidentalDeathDismemberment: freeText("accidentalDeathDismemberment"),
  lacerationsMaximum: maxBenefit(
    "lacerationsMaximumType",
    "lacerationsMaximumAmount",
  ),
  coma: maxBenefit("comaType", "comaAmount"),
  majorDiagnosticExamXray: maxBenefit(
    "majorDiagnosticExamXrayType",
    "majorDiagnosticExamXrayAmount",
  ),
  majorDiagnosticExamMri: maxBenefit(
    "majorDiagnosticExamMriType",
    "majorDiagnosticExamMriAmount",
  ),
  wellnessBenefit: specificBenefit(
    "wellnessBenefitType",
    "wellnessBenefitAmount",
  ),
  ageReductionSchedule: formatAgeReductionSchedule,
  childOrganizedSportsEnhancement: enumLabel(
    "childOrganizedSportsEnhancement",
    INCLUSION_LABELS,
  ),
  portability: enumLabel("portability", INCLUSION_LABELS),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
};

export const formatAccidentField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = accidentFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
